import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { View } from "ui/atoms/view";
import { Text } from "react-native";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import { AuditLogModel } from "app/services/api/types/auditLogs/models/AuditLogModel";
import { format, parse } from "date-fns";
import { Button, ButtonText } from "ui/gluestack/button";

export interface AuditLogDetailsModalRef {
  show: (log: AuditLogModel) => void;
  close: () => void;
}

function JsonBlock({ title, data }: { title: string; data: unknown }) {
  const json = useMemo(() => {
    try {
      return JSON.stringify(data ?? {}, null, 2);
    } catch {
      return String(data);
    }
  }, [data]);

  return (
    <View className="flex-1 border rounded border-gray-200 overflow-hidden">
      <View className="px-3 py-2 border-b border-gray-200 bg-gray-50">
        <Text className="text-xs font-semibold text-gray-700">{title}</Text>
      </View>
      <View className="p-3">
        <Text className="font-mono text-[11px] leading-4 text-gray-800 whitespace-pre-wrap">
          {json}
        </Text>
      </View>
    </View>
  );
}

export const AuditLogDetailsModal = forwardRef<AuditLogDetailsModalRef, {}>(
  (_props, ref) => {
    const [showing, setShowing] = useState(false);
    const [log, setLog] = useState<AuditLogModel | null>(null);

    useImperativeHandle(ref, () => ({
      show: (l: AuditLogModel) => {
        setLog(l);
        setShowing(true);
      },
      close: () => setShowing(false),
    }));

    const formattedDate = useMemo(() => {
      if (!log?.log_date) return "";
      try {
        return format(
          parse(log.log_date, "yyyy-MM-dd HH:mm:ss", new Date()),
          "dd/MM/yyyy HH:mm:ss",
        );
      } catch {
        return log.log_date;
      }
    }, [log?.log_date]);

    if (!log) return null;
    return (
      <ReusableModal
        isVisible={showing}
        onClose={() => setShowing(false)}
        title={`Audit Log Details #${log?.id}`}
        className="w-full max-w-[900px] mx-auto"
      >
        <View className="gap-8">
          {/* Summary */}
          <View className="grid grid-cols-2 gap-3 gap-x-5">
            <InfoRow label="Date & Time" value={formattedDate} />
            <InfoRow label="Action" value={log.type} />
            <InfoRow label="User" value={log.user_name ?? "-"} />
            <InfoRow label="Email" value={log.user_email ?? "-"} />
            <InfoRow label="Model type" value={log.model_type} />
            <InfoRow label="Model name" value={log.model_display_name ?? "-"} />
            <InfoRow label="Model ID" value={String(log.model_id ?? "-")} />
            <InfoRow label="Table" value={log.table_name} />
            <InfoRow label="IP" value={log.ip ?? "-"} />
            <InfoRow label="Browser" value={log.browser_name ?? "-"} />
            <InfoRow label="Platform" value={log.platform ?? "-"} />
            <InfoRow label="Device" value={log.device ?? "-"} />
          </View>

          {/* Dirty keys */}
          {log.dirty_keys?.length ? (
            <View>
              <Text className="text-xs font-semibold text-gray-700 mb-1">
                Changed fields
              </Text>
              <Text className="text-xs text-gray-800">
                {log.dirty_keys.join(", ")}
              </Text>
            </View>
          ) : null}

          {/* Data comparison */}
          <View className="gap-2">
            <Text className="text-sm font-semibold text-gray-800">
              Data changes
            </Text>
            <View className="flex flex-row gap-3">
              {log.data?.oldContent ? (
                <JsonBlock title="Old data" data={log.data.oldContent} />
              ) : null}
              <JsonBlock title="New data" data={log.data?.newContent} />
            </View>
          </View>
        </View>
      </ReusableModal>
    );
  },
);

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View className="flex-row justify-between gap-3 border-b py-1 border-gray-100">
      <Text className="text-xs text-gray-600">{label}</Text>
      <Text className="text-xs font-medium text-gray-900">{value}</Text>
    </View>
  );
}

AuditLogDetailsModal.displayName = "AuditLogDetailsModal";

export default AuditLogDetailsModal;
